import React from "react"
import { useSelector, useDispatch } from "react-redux"

import { CcRequirementVersion } from "server/models"
import {
    StoreType,
    addModal,
} from "client/store"
import {
    Link,
    DateFormatter,
    TableColumn,
    TablePopover,
    TablePopoverData,
    UserIcon,
    NotAssigned,
    Empty,
} from "client/components"

import VersionView from "../versionView"

const Columns = () => {
    const userList = useSelector((state: StoreType) => state.user.userList)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const versionLink = (version: CcRequirementVersion, linkContent: any) => (
        <Link
            href="/#"
            onClick={() => dispatch(addModal({
                key: "viewVersion",
                content: <VersionView version={version} />,
            }))}
        >
            {linkContent}
        </Link>
    )

    const columns: TableColumn[] = [
        {
            field: "revision",
            header: lang.revision,
            cellMinWidth: "100px",
            cellMaxWidth: "150px",
        },
        {
            field: "uniqueId",
            header: lang.uniqueId,
            cellMinWidth: "150px",
            cellMaxWidth: "250px",
            formatter: (version: CcRequirementVersion) => versionLink(version, version.uniqueId),
        },
        {
            field: "name",
            header: lang.name,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
            formatter: (version: CcRequirementVersion) => versionLink(version, version.name),
        },
        {
            field: "creator",
            header: lang.createdBy,
            cellMinWidth: "150px",
            formatter: (version: CcRequirementVersion) => {
                const user = userList[version.creator]
                return <UserIcon user={user} size="sm" />
            },
        },
        {
            field: "type",
            header: lang.type,
            cellMinWidth: "150px",
            formatter: (version: CcRequirementVersion) => {
                const { typeParsed } = version
                return typeParsed ? typeParsed.name : <NotAssigned />
            },
        },
        {
            field: "priority",
            header: lang.priority,
            cellMinWidth: "150px",
            formatter: (version: CcRequirementVersion) => {
                const { priorityParsed } = version
                return priorityParsed ? priorityParsed.name : <NotAssigned />
            },
        },
        {
            field: "status",
            header: lang.status,
            cellMinWidth: "150px",
            formatter: (version: CcRequirementVersion) => {
                const { statusParsed } = version
                return statusParsed ? statusParsed.name : <NotAssigned />
            },
        },
        {
            field: "reviewMethods",
            header: lang.reviewMethods,
            cellMinWidth: "200px",
            formatter: (version: CcRequirementVersion) => {
                const { reviewMethodsParsed } = version
                if (!reviewMethodsParsed || !reviewMethodsParsed.length) return <Empty />

                let enabledCount = 0
                const dataList: TablePopoverData[] = []
                reviewMethodsParsed.forEach((method) => {
                    if (method.enabled) {
                        enabledCount += 1
                        dataList.push({
                            id: `${version.id}_${method.id}`,
                            title: method.name,
                        })
                    }
                })

                if (enabledCount === 0) return <Empty />
                return (
                    <TablePopover
                        id={`reviewMethods${version.id}`}
                        dataList={dataList}
                        placement="right"
                        title={lang.reviewMethods}
                        showFirst={dataList.length === 1}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "smartCompliance",
            header: lang.smartCompliance,
            cellMinWidth: "200px",
            formatter: (version: CcRequirementVersion) => {
                const { smartComplianceParsed } = version
                if (!smartComplianceParsed || !smartComplianceParsed.length) return <Empty />

                let enabledCount = 0
                const dataList: TablePopoverData[] = []
                smartComplianceParsed.forEach((compliance) => {
                    if (compliance.enabled) {
                        enabledCount += 1
                        dataList.push({
                            id: `${version.id}_${compliance.id}`,
                            title: compliance.name,
                        })
                    }
                })

                if (enabledCount === 0) return <Empty />
                return (
                    <TablePopover
                        id={`smartCompliance${version.id}`}
                        dataList={dataList}
                        placement="right"
                        title={lang.smartCompliance}
                        showFirst={dataList.length === 1}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "createdAt",
            header: lang.created,
            formatter: (version: CcRequirementVersion) => <DateFormatter date={version.createdAt} />,
        },
    ]

    return columns
}
export default Columns
